import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Checkbox, DatePicker, Space, Table, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { api } from '../lib/api';
import type { ExpiredAccessReportRow } from '../types';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

function formatDateTime(value: string) {
  return new Date(value).toLocaleString();
}

function csvEscape(value: string | null | undefined) {
  const text = value || '';
  return `"${text.replace(/"/g, '""')}"`;
}

function downloadCsv(rows: ExpiredAccessReportRow[]) {
  const header = [
    'User name',
    'User email',
    'Organization',
    'Registered from',
    'Subscription/trial expired at',
  ];
  const body = rows.map((row) => [
    row.userName || '',
    row.userEmail,
    row.organization || '',
    formatDateTime(row.registeredAt),
    formatDateTime(row.expiredAt),
  ]);
  const csv = [header, ...body]
    .map((line) => line.map((value) => csvEscape(value)).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `expired-access-report-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export default function AnalyticsExpiredAccessReportPage() {
  const [range, setRange] = useState<[Dayjs, Dayjs]>(() => [dayjs().subtract(30, 'day'), dayjs()]);
  const [ignoreAnnea, setIgnoreAnnea] = useState(true);
  const [rows, setRows] = useState<ExpiredAccessReportRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastGenerated, setLastGenerated] = useState<string | null>(null);

  useEffect(() => {
    generateReport();
  }, []);

  const generateReport = async () => {
    try {
      setLoading(true);
      const data = await api.analytics.expiredAccessReport({
        from: range[0].startOf('day').toISOString(),
        to: range[1].endOf('day').toISOString(),
        ignoreAnnea,
      });
      setRows(data.rows || []);
      setLastGenerated(new Date().toLocaleString());
    } catch (error: any) {
      message.error(error.message || 'Failed to generate report');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const columns: ColumnsType<ExpiredAccessReportRow> = useMemo(() => [
    {
      title: 'User name',
      dataIndex: 'userName',
      key: 'userName',
      render: (value) => value || '-',
    },
    {
      title: 'User email',
      dataIndex: 'userEmail',
      key: 'userEmail',
    },
    {
      title: 'Organization',
      dataIndex: 'organization',
      key: 'organization',
      render: (value) => value || '-',
    },
    {
      title: 'Registered from',
      dataIndex: 'registeredAt',
      key: 'registeredAt',
      render: formatDateTime,
    },
    {
      title: 'Subscription/trial expired at',
      dataIndex: 'expiredAt',
      key: 'expiredAt',
      render: formatDateTime,
      sorter: (a, b) => new Date(a.expiredAt).getTime() - new Date(b.expiredAt).getTime(),
      defaultSortOrder: 'descend',
    },
  ], []);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <Title level={2} style={{ marginBottom: 4 }}>Expired Subscriptions Report</Title>
          <Text type="secondary">
            Users whose personal subscription/trial or assigned organization seat expired in the selected period (and have no active trial or subscription)
            {lastGenerated ? ` · Generated ${lastGenerated}` : ''}
          </Text>
        </div>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Space size="middle" wrap align="end">
          <div>
            <Text type="secondary">Expired between</Text>
            <RangePicker
              value={range}
              onChange={(value) => {
                if (value?.[0] && value?.[1]) {
                  setRange([value[0], value[1]]);
                }
              }}
              allowClear={false}
            />
          </div>
          <Checkbox
            checked={ignoreAnnea}
            onChange={(event) => setIgnoreAnnea(event.target.checked)}
          >
            Ignore Annea users
          </Checkbox>
          <Button type="primary" onClick={() => void generateReport()} loading={loading}>
            Generate Report
          </Button>
          <Button onClick={() => downloadCsv(rows)} disabled={rows.length === 0}>
            Download CSV
          </Button>
        </Space>
      </Card>

      <Card>
        <Table
          rowKey="userId"
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={{ pageSize: 50, showSizeChanger: true }}
          locale={{ emptyText: 'No expired subscriptions or trials in this period.' }}
        />
      </Card>
    </div>
  );
}
